/**
 * PlanningPermissionApplication entity model class
 *
 * This file is part of ePlan
 * (c) Kaden und Partner AG
 */
import { BaseModel } from './BaseModel';
import { BaseCollection } from './BaseCollection';
import { getI18n } from '@/helpers/i18n';
import type { MutationsObject } from '@kp/js-model';
import dayjs, { Dayjs } from 'dayjs';

export const BAUVORHABEN = () => [
    {
        value: 'building_new',
        text: getI18n().t('building_new'),
        showTextfield: false,
        showNutzungsziffer: true
    },
    {
        value: 'building_demolition',
        text: getI18n().t('building_demolition'),
        showNutzungsziffer: false
    },
    {
        value: 'building_extension',
        text: getI18n().t('building_extension'),
        showNutzungsziffer: true
    },
    {
        value: 'building_conversion',
        text: getI18n().t('building_conversion'),
        showNutzungsziffer: false
    },
    {
        value: 'building_purpose_change',
        text: getI18n().t('building_purpose_change'),
        showNutzungsziffer: false
    },
    {
        value: 'building_reconstruction',
        text: getI18n().t('building_reconstruction'),
        showNutzungsziffer: true
    }
];

export const ZWECKBESTIMMUNG = () => [
    { value: 'residential_use', text: getI18n().t('residential_use') },
    { value: 'commercial_and_industrial_use', text: getI18n().t('commercial_and_industrial_use') },
    { value: 'agricultural_use', text: getI18n().t('agricultural_use') },
    { value: 'public_use', text: getI18n().t('public_use') },
    { value: 'other_use', text: getI18n().t('other_use') }
];

export class PlanningPermissionApplication extends BaseModel {
    id: number | null = null;
    created: Dayjs | null = null;
    creator_id = null;
    modified: Dayjs | null = null;
    modifier_id = null;
    type: string | null = null;
    boa_id = null;
    description = null;
    application_type_id = null;
    application_type_bezeichnung = null;
    remark = null;
    proceeding_type = null;
    profiling_yes_no: boolean | null = null;
    profiling_date: Dayjs | null = null;
    has_open_appeals = null;
    intended_purpose: string[] | null = null;
    parking_lots_yes_no = null;
    construction_cost = null;
    dossier_identification = null;
    gemeinde_id = null;
    stelle_id: number | null = null;
    business_type_ids: number[] = [];
    deleted = null;
    boa_bgnumber = null;
    location_address_id = null;
    dossier_nr_gemeinde = null;
    reference_planning_permission_application_id: number | null = null;
    beantragter_status_verlauf_id = null;
    estimated_end_date_kanton = null;
    archived_at = null;
    uuid = null;
    project_author_equals_applicant: boolean | null = null;
    landowner_equals_applicant: boolean | null = null;
    invoice_recipient_equals_applicant: boolean | null = null;
    clerk_equals_applicant: boolean | null = null;

    // view
    stelle = null;
    stelle_active = null;
    stelle_uid = null;
    application_type = null;
    aktueller_status_id = null;
    aktueller_status = null;
    aktueller_status_ech_value = null;
    aktueller_status_ebau_value: string | null = null;
    aktueller_status_verlauf_id = null;
    business_type = null;
    business_type_array: string[] | null = null;

    construction_project_type: string[] | null = null;
    construction_project_type_other_description = null;

    decision_ruling_ruling_date = null;
    decision_ruling_judgement_type_ech_value = null;
    decision_ruling_judgement_type_display_value = null;
    decision_ruling_judgement_type_is_positive = null;

    open_circulation_count = null;
    open_stelle_ids = [];
    circulated_to_kanton = false;

    has_open_sistierung_requests = false;

    beantragter_status_ech_value = null;
    beantragter_status_ebau_value = null;

    location_street = null;
    location_house_number = null;
    location_town = null;
    location_swiss_zip_code = null;
    location_egrid = null;
    location_coordinate_east = null;
    location_coordinate_north = null;
    location_insurance_number = null;

    first_submitted_date = null;
    last_modified = null;

    applicants: string[] | null = null;
    writers: number[] | null = null;
    write_gemeinden = null;

    public mutations(): MutationsObject {
        return {
            id: (id) => (Number(id) > 0 ? Number(id) : null),
            created: (value) => dayjs(value),
            modified: (value) => dayjs(value),
            profiling_date: (value) => dayjs(value),
            estimated_end_date_kanton: (value) => dayjs(value),
            last_modified: (value) => dayjs(value),
        };
    }

    getClassName(): string {
        return 'PlanningPermissionApplication';
    }
}

export class PlanningPermissionApplicationCollection extends BaseCollection<PlanningPermissionApplication> {
    protected modelCls = PlanningPermissionApplication;

    public getModelClassName(): string {
        return 'PlanningPermissionApplication';
    }
}
